/**
 * MKS-406 — "Shared labels" on the project edit form: switch general global task
 * labels (BE, FE, Bug, ...) off for THIS project only. A switched-off label leaves
 * the project's label picker; a label already on a task stays on it.
 *
 * Saves through its own endpoint on every toggle, like the in-progress-roles
 * checkboxes next to it. Audience is admin or the project's PM (the form decides
 * who sees it; the API re-checks).
 */
import {
  useProjectSharedTaskLabels,
  useSetProjectDisabledTaskLabels,
} from '../../hooks/useTaskLabels';

export interface ProjectSharedLabelsSectionProps {
  projectId: number;
}

export function ProjectSharedLabelsSection({ projectId }: ProjectSharedLabelsSectionProps) {
  const { data: labels, isLoading, isError } = useProjectSharedTaskLabels(projectId);
  const setDisabled = useSetProjectDisabledTaskLabels();

  const toggle = (labelId: number) => {
    const disabledIds = (labels ?? []).filter((l) => !l.enabled).map((l) => l.id);
    const next = disabledIds.includes(labelId)
      ? disabledIds.filter((id) => id !== labelId)
      : [...disabledIds, labelId];
    setDisabled.mutate({ projectId, disabledLabelIds: next });
  };

  return (
    <section className="mt-8 space-y-4 border-t border-gray-100 pt-6" data-section="shared-labels">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Shared labels</h2>
        <p className="text-xs text-gray-500 mt-1">
          System-wide labels offered in this project&apos;s label picker. Unchecking one hides it
          here only; labels already on tasks stay.
        </p>
      </div>

      {isLoading && <p className="text-xs text-gray-400">Loading labels…</p>}
      {isError && <p className="text-xs text-red-600">Could not load shared labels.</p>}
      {labels && labels.length === 0 && (
        <p className="text-xs text-gray-400">No shared labels exist yet.</p>
      )}

      {labels && labels.length > 0 && (
        <div className="flex flex-col gap-2">
          {labels.map((label) => (
            <label key={label.id} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={label.enabled}
                disabled={setDisabled.isPending}
                onChange={() => toggle(label.id)}
                data-testid={`shared-label-${label.id}`}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: label.color }}
              />
              {label.name}
            </label>
          ))}
        </div>
      )}

      {setDisabled.isError && (
        <p className="text-xs text-red-600" role="alert">
          {setDisabled.error.message}
        </p>
      )}
    </section>
  );
}
